/**
 * `<HelpdeskTeamsPage>` — `/w/:slug/helpdesk/teams`. Routing groups
 * ("Billing", "Deliveries") the AI sends handoffs to, managed alongside the
 * desk's Members + Roles pages (moved here from Workspace → Helpdesk settings
 * on 2026-07-15 user request). Storybook `TeamsSection` table minus the hours
 * columns, over the real `/api/teams` CRUD.
 *
 * The member picker lists the DESK ROSTER (helpdesk members), not agent
 * profiles: adding someone to a team auto-provisions their `AgentProfile`
 * (default unavailable / cap 3) when they don't have one yet, because a
 * routing team's members must be operational agents. Gated
 * `handoff:configure`, same as the teams API.
 */
import { useAbility, useWorkspace } from "@cometchat-ai/auth/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  TableBody,
  TableHeader,
  Textarea,
  cn,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Pencil, Plus, Trash2, Users } from "../components/icons";
import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { PageShell } from "../components/page-shell";
import { formatApiError } from "../lib/format-error";
import {
  type AgentProfile,
  type Team,
  agentProfileKeys,
  createAgentProfile,
  createTeam,
  deleteTeam,
  listAgentProfiles,
  listTeams,
  setTeamMembers,
  teamKeys,
  updateTeam,
} from "../handoff/api";
import { type HelpdeskMember, helpdeskTeamKeys, listHelpdeskMembers } from "./team-api";

const MAX_AVATARS = 5;

/** Initials avatar + availability dot (green available / grey unavailable). */
function MemberAvatar({
  name,
  availability,
}: {
  name: string;
  availability: "available" | "unavailable";
}) {
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span className="relative rounded-full ring-2 ring-background" title={name}>
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
        {initials || "?"}
      </span>
      <span
        className={cn(
          "absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-background",
          availability === "available"
            ? "bg-[color:var(--color-success)]"
            : "bg-[color:var(--color-fg-subtle)]",
        )}
        aria-hidden
      />
    </span>
  );
}

export function HelpdeskTeamsPage() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const ability = useAbility();
  const canConfigure = ability.can("configure", "HandoffEvent");
  const queryClient = useQueryClient();

  const teamsQ = useQuery({
    queryKey: teamKeys.list(slug),
    queryFn: () => listTeams(),
    enabled: canConfigure,
  });
  const profilesQ = useQuery({
    queryKey: agentProfileKeys.list(slug),
    queryFn: () => listAgentProfiles(),
    enabled: canConfigure,
  });
  const deskMembersQ = useQuery({
    queryKey: helpdeskTeamKeys.members(slug),
    queryFn: () => listHelpdeskMembers(slug),
    enabled: canConfigure,
  });

  const [dialog, setDialog] = useState<{ team: Team | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: teamKeys.list(slug) });
    void queryClient.invalidateQueries({ queryKey: agentProfileKeys.list(slug) });
  };

  const remove = useMutation({
    mutationFn: (id: string) => deleteTeam(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("helpdeskSettings.teams.deletedToast"));
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const teams = teamsQ.data?.items ?? [];

  if (!canConfigure) {
    return (
      <p className="px-6 py-12 text-center text-2sm text-muted-foreground">
        {t("helpdeskSettings.forbidden")}
      </p>
    );
  }

  return (
    <PageShell
      className="p-0"
      data-testid="helpdesk-teams-page"
      description={t("helpdeskSettings.teams.description")}
      actions={
        <Button
          size="sm"
          className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
          onClick={() => setDialog({ team: null })}
          data-testid="helpdesk-teams-add"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t("helpdeskSettings.teams.add")}
        </Button>
      }
    >
      <div className="flex flex-col py-2">
        {teamsQ.isPending ? (
          <ListSkeleton rows={3} itemClassName="h-10 w-full" className="space-y-2 px-6" />
        ) : teamsQ.isError ? (
          <div className="px-6">
            <ErrorState message={formatApiError(teamsQ.error)} onRetry={teamsQ.refetch} />
          </div>
        ) : teams.length === 0 ? (
          <div className="px-6 py-12">
            <EmptyState icon={Users} title={t("helpdeskSettings.teams.empty")} />
          </div>
        ) : (
          <DataGrid className="border-t border-border">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6">
                  {t("helpdeskSettings.teams.table.team")}
                </DataGridHead>
                <DataGridHead>{t("helpdeskSettings.teams.table.skills")}</DataGridHead>
                <DataGridHead className="w-[180px]">
                  {t("helpdeskSettings.teams.table.members")}
                </DataGridHead>
                <DataGridHead className="w-[90px] pr-6 text-right" divider={false}>
                  {t("helpdeskSettings.teams.table.actions")}
                </DataGridHead>
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {teams.map((team) => (
                <DataGridRow key={team.id} data-testid={`helpdesk-team-row-${team.tag}`}>
                  <DataGridCell className="pl-6">
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-foreground">
                        {team.name}
                      </div>
                      <div className="truncate text-[12px] text-muted-foreground">#{team.tag}</div>
                    </div>
                  </DataGridCell>
                  <DataGridCell className="max-w-[320px]">
                    <span className="line-clamp-2 text-[12px] text-muted-foreground">
                      {team.description || "—"}
                    </span>
                  </DataGridCell>
                  <DataGridCell>
                    {team.members.length === 0 ? (
                      <span className="text-[13px] text-muted-foreground">—</span>
                    ) : (
                      <div className="flex items-center -space-x-1.5">
                        {team.members.slice(0, MAX_AVATARS).map((m) => (
                          <MemberAvatar
                            key={m.agentProfileId}
                            name={m.displayName ?? "?"}
                            availability={m.availability}
                          />
                        ))}
                        {team.members.length > MAX_AVATARS ? (
                          <span
                            className="relative flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-background"
                            title={team.members
                              .slice(MAX_AVATARS)
                              .map((m) => m.displayName ?? "?")
                              .join(", ")}
                          >
                            +{team.members.length - MAX_AVATARS}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </DataGridCell>
                  <DataGridCell className="pr-6 text-right" divider={false}>
                    <div className="flex items-center justify-end gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-md text-[color:var(--color-fg-subtle)] [&_svg]:size-3.5"
                        onClick={() => setDialog({ team })}
                        aria-label={t("helpdeskSettings.teams.edit")}
                        data-testid={`helpdesk-team-edit-${team.tag}`}
                      >
                        <Pencil aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-md text-[color:var(--color-fg-subtle)] [&_svg]:size-3.5"
                        onClick={() => setDeleteTarget(team)}
                        aria-label={t("helpdeskSettings.teams.delete")}
                        data-testid={`helpdesk-team-delete-${team.tag}`}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    </div>
                  </DataGridCell>
                </DataGridRow>
              ))}
            </TableBody>
          </DataGrid>
        )}
      </div>

      {dialog ? (
        <TeamDialog
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
          team={dialog.team}
          deskMembers={deskMembersQ.data ?? []}
          profiles={profilesQ.data?.items ?? []}
          onSaved={invalidate}
        />
      ) : null}

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("helpdeskSettings.teams.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("helpdeskSettings.teams.deleteDescription", { name: deleteTarget?.name ?? "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("helpdeskSettings.teams.dialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              {t("helpdeskSettings.teams.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

/**
 * Create/edit dialog. Members are picked from the DESK ROSTER (userIds);
 * on save, anyone without an `AgentProfile` gets one auto-provisioned
 * (default unavailable / cap 3) before the roster is written.
 */
function TeamDialog({
  open,
  onOpenChange,
  team,
  deskMembers,
  profiles,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  team: Team | null;
  deskMembers: HelpdeskMember[];
  profiles: AgentProfile[];
  onSaved: () => void;
}) {
  const { t } = useTranslation("workspace");
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [tagTouched, setTagTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [memberUserIds, setMemberUserIds] = useState<Set<string>>(new Set());

  const profileByUserId = useMemo(() => new Map(profiles.map((p) => [p.userId, p])), [profiles]);

  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? "");
    setTag(team?.tag ?? "");
    setTagTouched(team !== null);
    setDescription(team?.description ?? "");
    setMemberUserIds(new Set(team?.members.map((m) => m.userId) ?? []));
  }, [open, team]);

  const slugify = (v: string) =>
    v
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48);

  const save = useMutation({
    mutationFn: async () => {
      // Resolve every picked desk member to an AgentProfile, creating the
      // missing ones — a routing team's members must be operational agents.
      const profileIds: string[] = [];
      for (const userId of memberUserIds) {
        const existing = profileByUserId.get(userId);
        if (existing) {
          profileIds.push(existing.id);
        } else {
          const created = await createAgentProfile({ userId });
          profileIds.push(created.id);
        }
      }
      if (team) {
        await updateTeam(team.id, { name: name.trim(), tag, description: description.trim() });
        await setTeamMembers(team.id, profileIds);
      } else {
        await createTeam({
          name: name.trim(),
          tag,
          description: description.trim(),
          agentProfileIds: profileIds,
        });
      }
    },
    onSuccess: () => {
      onSaved();
      toast.success(
        team ? t("helpdeskSettings.teams.updatedToast") : t("helpdeskSettings.teams.createdToast"),
      );
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const valid = useMemo(
    () => name.trim().length > 0 && /^[a-z0-9][a-z0-9_-]{0,47}$/.test(tag),
    [name, tag],
  );

  const toggleMember = (userId: string) =>
    setMemberUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-0 rounded-2xl p-0">
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-semibold tracking-wide-sm">
            {team
              ? t("helpdeskSettings.teams.dialog.editTitle")
              : t("helpdeskSettings.teams.dialog.addTitle")}
          </DialogTitle>
          <DialogDescription className="text-2sm text-muted-foreground">
            {t("helpdeskSettings.teams.dialog.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-5 pb-1">
          <div className="space-y-1.5">
            <Label className="text-2sm font-medium text-foreground" htmlFor="team-name">
              {t("helpdeskSettings.teams.dialog.name")}
            </Label>
            <Input
              id="team-name"
              value={name}
              maxLength={80}
              onChange={(e) => {
                setName(e.target.value);
                if (!tagTouched) setTag(slugify(e.target.value));
              }}
              placeholder={t("helpdeskSettings.teams.dialog.namePlaceholder")}
              className="h-9 rounded-lg text-2sm"
              data-testid="team-dialog-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-2sm font-medium text-foreground" htmlFor="team-tag">
              {t("helpdeskSettings.teams.dialog.tag")}
            </Label>
            <Input
              id="team-tag"
              value={tag}
              maxLength={48}
              onChange={(e) => {
                setTagTouched(true);
                setTag(slugify(e.target.value));
              }}
              placeholder={t("helpdeskSettings.teams.dialog.tagPlaceholder")}
              className="h-9 rounded-lg font-mono text-2sm"
              data-testid="team-dialog-tag"
            />
            <p className="text-2xs text-[color:var(--color-fg-subtle)]">
              {t("helpdeskSettings.teams.dialog.tagHint")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-2sm font-medium text-foreground" htmlFor="team-description">
              {t("helpdeskSettings.teams.dialog.skills")}
            </Label>
            <Textarea
              id="team-description"
              value={description}
              maxLength={2000}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("helpdeskSettings.teams.dialog.skillsPlaceholder")}
              className="rounded-lg text-2sm"
              data-testid="team-dialog-description"
            />
            <p className="text-2xs text-[color:var(--color-fg-subtle)]">
              {t("helpdeskSettings.teams.dialog.skillsHint")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-2sm font-medium text-foreground">
              {t("helpdeskSettings.teams.dialog.members")}
            </Label>
            {deskMembers.length === 0 ? (
              <p className="text-2sm text-muted-foreground">
                {t("helpdeskSettings.teams.dialog.noMembers")}
              </p>
            ) : (
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {deskMembers.map((m) => {
                  const memberName =
                    [m.firstName, m.lastName].filter(Boolean).join(" ").trim() || m.email;
                  const profile = profileByUserId.get(m.userId);
                  return (
                    <label
                      key={m.userId}
                      className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-2sm hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={memberUserIds.has(m.userId)}
                        onCheckedChange={() => toggleMember(m.userId)}
                        data-testid={`team-dialog-member-${m.userId}`}
                      />
                      <span className="min-w-0 flex-1 truncate text-foreground">{memberName}</span>
                      <span
                        className={cn(
                          "h-1.5 w-1.5 shrink-0 rounded-full",
                          profile?.availability === "available"
                            ? "bg-[color:var(--color-success)]"
                            : "bg-[color:var(--color-fg-subtle)]",
                        )}
                        aria-hidden
                      />
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-2xs text-[color:var(--color-fg-subtle)]">
              {t("helpdeskSettings.teams.dialog.membersHint")}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 px-5 pb-5 pt-4">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 rounded-lg px-4"
            onClick={() => onOpenChange(false)}
          >
            {t("helpdeskSettings.teams.dialog.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-9 rounded-lg px-4"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
            data-testid="team-dialog-save"
          >
            {team
              ? t("helpdeskSettings.teams.dialog.save")
              : t("helpdeskSettings.teams.dialog.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
